import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConsultantNav } from "@/components/ConsultantNav";
import { toast } from "sonner";
import {
  Lock, Loader2, LogOut, ArrowLeft, Plus, Copy, CheckCircle2, Users, Building2,
  LayoutGrid, Target, TrendingUp, Upload, Rocket, AlertTriangle, Send, Bell, X, Star,
  FileText, ShieldCheck, ShieldAlert, Trash2, Eye, Clock, FileUp,
  Workflow, GitBranch, ExternalLink, Sparkles, ChevronRight, Briefcase, Receipt, BadgeCheck,
  Download, Link2, FileSpreadsheet, Pencil, Save, ClipboardCheck,
} from "lucide-react";
import {
  consultantLogin, b2mOverview, b2mListCohorts, b2mCreateCohort,
  b2mCohortDetail, b2mAddParticipant, b2mImportParticipants, b2mMeta,
  b2mActions, b2mRunReminders, b2mSendCheckpoint, b2mParticipantDetail, b2mSaveNotes,
  b2mExportMeta, b2mListReqs, b2mCreateReq, b2mDeleteReq, b2mGetExport, b2mSaveExport,
  b2mCertList, b2mCertUpload, b2mCertUpdate, b2mCertDelete, b2mCertFileBlob,
  bgMeta, bgListCohorts, bgCreateCohort, bgCohortDetail, bgAddParticipant,
  bgSendOne, bgSendAll, bgBridge, bgDeleteCohort, bgDemoSeed, bgDemoDelete,
  b2mEviList, b2mEviUpload, b2mEviUpdate, b2mEviDelete, b2mEviFileBlob,
  b2mIndividualReport, b2mOrgReportPdf, b2mOrgCsv, b2mOrgXlsx,
  b2mOrgLinkIssue, b2mOrgLinkRevoke, b2mAgencyPack, b2mEcosystemPng, b2mSuccessStory,
  b2mUpdateParticipant, b2mDeleteParticipant, bgUpdateParticipant, bgDeleteParticipant,
  b2mImpactReview, b2mSaveImpactReview, b2mFinaliseImpact,
} from "@/lib/gfApi";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const EVI_STATUS_COLOR = { "Disahkan": "#2FA89F", "Disemak": "#6B8FD6", "Belum Disemak": "#D6A25A" };

const MODAL_INK = "#141A32";
const MODAL_INK2 = "#1E2647";
const MODAL_GOLD = "#D6A25A";
const MODAL_TEAL = "#2FA89F";
const mfield = "w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none";
const mfieldStyle = { borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff" };

/* Edit / Delete a single participant (works for both B2M and BG). */
function ParticipantEditModal({ kind, participant, meta, token, onClose }) {
  const isB2M = kind === "b2m";
  const [f, setF] = useState({
    name: participant.name || "", email: participant.email || "", phone: participant.phone || "",
    business_name: participant.business_name || "",
    industri: participant.industri || "",
    sector: participant.sector || "Other",
    product_category: participant.product_category || "",
    primary_target_market: participant.primary_target_market || "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama peserta diperlukan."); return; }
    setSaving(true);
    try {
      if (isB2M) {
        await b2mUpdateParticipant(participant.id, {
          name: f.name, email: f.email, phone: f.phone, business_name: f.business_name,
          sector: f.sector, product_category: f.product_category,
          primary_target_market: f.primary_target_market,
          target_markets: f.primary_target_market ? [f.primary_target_market] : [],
        }, token);
      } else {
        await bgUpdateParticipant(participant.id, {
          name: f.name, email: f.email, phone: f.phone,
          business_name: f.business_name, industri: f.industri,
        }, token);
      }
      toast.success("Butiran peserta dikemas kini.");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  const del = async () => {
    setDeleting(true);
    try {
      if (isB2M) await b2mDeleteParticipant(participant.id, token);
      else await bgDeleteParticipant(participant.id, token);
      toast.success("Peserta dipadam.");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memadam."); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={() => onClose(false)} data-testid="participant-edit-modal">
      <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: MODAL_INK2, border: "1px solid rgba(214,162,90,0.3)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-white">Sunting Butiran Peserta</h3>
          <button onClick={() => onClose(false)} data-testid="pe-close" className="rounded-full p-1.5 text-white/70 hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Nama</label>
            <input data-testid="pe-name" className={mfield} style={mfieldStyle} value={f.name} onChange={set("name")} /></div>
          <div><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Emel</label>
            <input data-testid="pe-email" className={mfield} style={mfieldStyle} value={f.email} onChange={set("email")} /></div>
          <div><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Telefon</label>
            <input data-testid="pe-phone" className={mfield} style={mfieldStyle} value={f.phone} onChange={set("phone")} /></div>
          <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Nama Bisnes</label>
            <input data-testid="pe-biz" className={mfield} style={mfieldStyle} value={f.business_name} onChange={set("business_name")} /></div>
          {isB2M ? (
            <>
              <div><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Sektor</label>
                <select data-testid="pe-sector" className={mfield} style={mfieldStyle} value={f.sector} onChange={set("sector")}>
                  {((meta && meta.sectors) || [f.sector]).map((s) => <option key={s} value={s} style={{ color: "#000" }}>{s}</option>)}
                </select></div>
              <div><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Kategori Produk</label>
                <input data-testid="pe-category" className={mfield} style={mfieldStyle} value={f.product_category} onChange={set("product_category")} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Pasaran Sasaran Utama</label>
                <select data-testid="pe-market" className={mfield} style={mfieldStyle} value={f.primary_target_market} onChange={set("primary_target_market")}>
                  <option value="" style={{ color: "#000" }}>—</option>
                  {((meta && meta.markets) || []).map((m) => <option key={m} value={m} style={{ color: "#000" }}>{m}</option>)}
                </select></div>
            </>
          ) : (
            <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Industri</label>
              <input data-testid="pe-industri" className={mfield} style={mfieldStyle} value={f.industri} onChange={set("industri")} /></div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          {!confirmDel ? (
            <button data-testid="pe-delete" onClick={() => setConfirmDel(true)}
              className="flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold" style={{ background: "rgba(217,112,90,0.15)", color: "#F0A090" }}>
              <Trash2 className="h-4 w-4" /> Padam
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: "#F0A090" }}>Pasti padam?</span>
              <button data-testid="pe-delete-confirm" onClick={del} disabled={deleting}
                className="rounded-full px-3 py-2 text-xs font-bold" style={{ background: "#D9705A", color: "#fff" }}>
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Ya, padam"}
              </button>
              <button onClick={() => setConfirmDel(false)} className="rounded-full px-3 py-2 text-xs text-white/70">Batal</button>
            </div>
          )}
          <button data-testid="pe-save" onClick={save} disabled={saving}
            className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-50" style={{ background: MODAL_GOLD, color: MODAL_INK }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
          </button>
        </div>
      </div>
    </div>
  );
}

/* Review & Edit the Organisation Impact Report, then Confirm & Send (finalise/lock). */
function ImpactReviewModal({ code, token, onClose }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    b2mImpactReview(code, token).then((r) => {
      setData(r);
      setRows(r.fields.map((f) => ({ ...f, input: f.override != null ? String(f.override) : "" })));
      setComments(r.trainer_comments || "");
    }).catch(() => toast.error("Gagal memuatkan laporan."));
  }, [code, token]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: MODAL_GOLD }} />
      </div>
    );
  }
  const finalised = data.finalised;

  const save = async () => {
    setSaving(true);
    try {
      const overrides = {};
      rows.forEach((r) => { overrides[r.key] = r.input.trim() === "" ? null : r.input.trim(); });
      await b2mSaveImpactReview(code, { overrides, trainer_comments: comments }, token);
      toast.success("Suntingan laporan disimpan.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  const finalise = async (val) => {
    if (val) { await save(); }
    setBusy(true);
    try {
      await b2mFinaliseImpact(code, val, token);
      toast.success(val ? "Laporan dimuktamadkan — sedia untuk dimuat turun." : "Laporan dibuka semula untuk suntingan.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal."); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try { const b = await b2mOrgReportPdf(code, token); downloadBlob(b, `b2m-impact-${code}.pdf`); }
    catch { toast.error("Gagal menjana PDF."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4" style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={onClose} data-testid="impact-review-modal">
      <div className="my-8 w-full max-w-2xl rounded-2xl p-6" style={{ background: MODAL_INK2, border: "1px solid rgba(214,162,90,0.3)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: MODAL_GOLD }}>
              <ClipboardCheck className="h-4 w-4" /> Semak & Sunting Laporan Impak
            </p>
            <h3 className="mt-1 font-heading text-lg font-bold text-white">{data.organisation} · {data.cohort_name}</h3>
          </div>
          <button onClick={onClose} data-testid="ir-close" className="rounded-full p-1.5 text-white/70 hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        {finalised && (
          <div className="mt-4 flex items-center gap-2 rounded-xl p-3 text-sm" style={{ background: "rgba(47,168,159,0.15)", color: MODAL_TEAL }} data-testid="ir-finalised-banner">
            <CheckCircle2 className="h-4 w-4" /> Laporan telah dimuktamadkan. Buka semula untuk menyunting.
          </div>
        )}

        {/* Editable numbers / scores */}
        <p className="mt-5 text-xs font-bold uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Nombor & Skor Peserta</p>
        <p className="mt-0.5 text-[11px]" style={{ color: "#6B769A" }}>Kosongkan untuk guna nilai dikira automatik. Sunting untuk membetulkan (cth: peserta dimasukkan dua kali).</p>
        <div className="mt-3 space-y-2" data-testid="ir-fields">
          {rows.map((r, i) => (
            <div key={r.key} className="flex items-center gap-3">
              <span className="flex-1 text-sm text-white">{r.label}</span>
              <span className="w-24 text-right text-xs" style={{ color: "#6B769A" }}>auto: {r.computed ?? "—"}</span>
              <input
                data-testid={`ir-field-${r.key}`}
                disabled={finalised}
                value={r.input}
                onChange={(e) => setRows((s) => s.map((x, j) => j === i ? { ...x, input: e.target.value } : x))}
                placeholder={r.computed != null ? String(r.computed) : "—"}
                className="w-24 rounded-lg border px-2.5 py-1.5 text-sm text-white outline-none disabled:opacity-50"
                style={{ borderColor: "rgba(214,162,90,0.4)", background: "rgba(255,255,255,0.05)" }}
              />
            </div>
          ))}
        </div>

        {/* Trainer's comments */}
        <p className="mt-6 text-xs font-bold uppercase tracking-wide" style={{ color: "#9AA6C2" }}>Ulasan Jurulatih (Trainer's Comments)</p>
        <textarea
          data-testid="ir-comments"
          disabled={finalised}
          rows={5}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Maklum balas peribadi untuk laporan organisasi ini…"
          className="mt-2 w-full rounded-xl border p-3 text-sm leading-relaxed text-white outline-none disabled:opacity-50"
          style={{ borderColor: "rgba(214,162,90,0.4)", background: "rgba(255,255,255,0.05)" }}
        />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {!finalised ? (
            <>
              <button data-testid="ir-save" onClick={save} disabled={saving}
                className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Draf
              </button>
              <button data-testid="ir-finalise" onClick={() => finalise(true)} disabled={busy || saving}
                className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50" style={{ background: MODAL_GOLD, color: MODAL_INK }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Sahkan & Muktamad
              </button>
            </>
          ) : (
            <>
              <button data-testid="ir-reopen" onClick={() => finalise(false)} disabled={busy}
                className="rounded-full px-5 py-2.5 text-sm font-bold text-white" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}>
                Buka Semula
              </button>
              <button data-testid="ir-download" onClick={downloadPdf} disabled={busy}
                className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50" style={{ background: MODAL_GOLD, color: MODAL_INK }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Muat Turun PDF Muktamad
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CERT_STATUS_COLOR = { "Disahkan": "#2FA89F", "Belum Disahkan": "#D6A25A", "Ditolak": "#D9705A" };
const EXPIRY_META = {
  expired: { color: "#D9705A", label: "Tamat tempoh" },
  d30: { color: "#D9705A", label: "≤30 hari" },
  d60: { color: "#D6A25A", label: "≤60 hari" },
  d90: { color: "#D6A25A", label: "≤90 hari" },
  ok: { color: "#2FA89F", label: "Sah" },
  none: { color: "#8A93B2", label: "Tiada tarikh" },
};

export function expiryText(exp) {
  if (!exp || exp.days_left == null) return "Tiada tarikh luput";
  if (exp.days_left < 0) return `Tamat tempoh ${Math.abs(exp.days_left)} hari lalu`;
  if (exp.days_left === 0) return "Luput hari ini";
  return `${exp.days_left} hari lagi`;
}

const GAP_COLOR = { "READY": "#2FA89F", "NEEDS ATTENTION": "#D6A25A", "CRITICAL GAP": "#D9705A", "TO VERIFY": "#8A93B2" };
const REQ_COLOR = { "Mandatory": "#D9705A", "Conditional": "#D6A25A", "Recommended": "#2FA89F", "To Verify": "#8A93B2" };

const CHECKPOINTS = [["pre", "PRE"], ["post", "POST"], ["day30", "D30"], ["day60", "D60"], ["day90", "D90"]];

const TOKEN_KEY = "gf_consultant_token";
const INK = "#141A32";
const INK2 = "#1E2647";
const GOLD = "#D6A25A";
const TEAL = "#2FA89F";
const CREAM = "#F7F5EF";

const field = "w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none";
const fieldStyle = { borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff" };
const labelCls = "text-[11px] font-bold uppercase tracking-wider";
const labelStyle = { color: "#9AA6C2" };

const FullLoader = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 className="h-7 w-7 animate-spin" style={{ color: GOLD }} />
  </div>
);

function LoginGate({ onLogin }) {
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!passcode.trim()) return;
    setBusy(true);
    try {
      const { token } = await consultantLogin(passcode.trim());
      sessionStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch {
      toast.error("Kod laluan tidak sah.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-6" style={{ background: INK }} data-testid="b2m-login">
      <div className="w-full max-w-sm rounded-3xl p-8" style={{ background: INK2, border: `1px solid rgba(214,162,90,0.25)` }}>
        <div className="mb-6 flex items-center gap-2">
          <Rocket className="h-6 w-6" style={{ color: GOLD }} />
          <span className="font-heading text-lg font-bold text-white">Brand2Market™</span>
        </div>
        <div className="mb-2 flex items-center gap-2" style={{ color: GOLD }}>
          <Lock className="h-4 w-4" /> <span className="text-sm font-bold">Konsultan</span>
        </div>
        <p className="mb-4 text-sm" style={{ color: "#9AA6C2" }}>Masukkan kod laluan konsultan untuk mengurus kohort Brand2Market.</p>
        <input data-testid="b2m-passcode-input" type="password" value={passcode}
          onChange={(e) => setPasscode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Kod laluan" className={field} style={fieldStyle} />
        <button data-testid="b2m-login-btn" onClick={submit} disabled={busy}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold disabled:opacity-60"
          style={{ background: GOLD, color: INK }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
        </button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div className="rounded-2xl p-4" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#9AA6C2" }}>{label}</p>
    <p className="mt-1 font-heading text-2xl font-bold text-white">{value}</p>
    {sub && <p className="mt-0.5 text-xs" style={{ color: TEAL }}>{sub}</p>}
  </div>
);

function CreateCohort({ token, onBack, onCreated }) {
  const [f, setF] = useState({ sponsoring_org: "", cohort_name: "", start_date: "", end_date: "", post_date: "", day30_date: "", day60_date: "", day90_date: "", consultant: "Irma Ariandi" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.sponsoring_org.trim() || !f.cohort_name.trim()) { toast.error("Organisasi & nama kohort diperlukan."); return; }
    setBusy(true);
    try { const c = await b2mCreateCohort(f, token); toast.success(`Kohort dicipta: ${c.access_code}`); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencipta kohort."); }
    finally { setBusy(false); }
  };
  const dateFields = [["start_date", "Mula"], ["end_date", "Tamat"], ["post_date", "POST"], ["day30_date", "Hari 30"], ["day60_date", "Hari 60"], ["day90_date", "Hari 90"]];
  return (
    <div className="mx-auto max-w-2xl px-6 py-10" data-testid="b2m-create-cohort">
      <button onClick={onBack} className="flex items-center gap-1 text-sm" style={{ color: "#9AA6C2" }}><ArrowLeft className="h-4 w-4" /> Kembali</button>
      <h1 className="mt-4 font-heading text-2xl font-bold text-white">Cipta Kohort Brand2Market</h1>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className={labelCls} style={labelStyle}>Organisasi Penaja</label>
          <input data-testid="b2m-cc-org" className={field} style={fieldStyle} value={f.sponsoring_org} onChange={set("sponsoring_org")} placeholder="cth: MARA" /></div>
        <div className="sm:col-span-2"><label className={labelCls} style={labelStyle}>Nama Kohort</label>
          <input data-testid="b2m-cc-name" className={field} style={fieldStyle} value={f.cohort_name} onChange={set("cohort_name")} placeholder="cth: Kohort 4 - 2026" /></div>
        {dateFields.map(([k, lbl]) => (
          <div key={k}><label className={labelCls} style={labelStyle}>Tarikh {lbl}</label>
            <input type="date" data-testid={`b2m-cc-${k}`} className={field} style={fieldStyle} value={f[k]} onChange={set(k)} /></div>
        ))}
        <div className="sm:col-span-2"><label className={labelCls} style={labelStyle}>Konsultan</label>
          <input className={field} style={fieldStyle} value={f.consultant} onChange={set("consultant")} /></div>
      </div>
      <button data-testid="b2m-cc-submit" onClick={submit} disabled={busy}
        className="mt-6 flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Cipta Kohort
      </button>
    </div>
  );
}

function ExportPanel({ pid, token, sector, market }) {
  const [b, setB] = useState(null);
  const [fields, setFields] = useState([]);
  const [ratings, setRatings] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    b2mExportMeta(token).then((m) => setFields(m.self_fields || [])).catch(() => {});
    b2mGetExport(pid, token).then((r) => { setB(r); setRatings(r.readiness ? {} : {}); }).catch(() => {});
  }, [pid, token]);
  const save = async () => {
    setSaving(true);
    try { const r = await b2mSaveExport(pid, { self_ratings: ratings }, token); setB(r); toast.success("Kesediaan eksport dikira."); }
    catch { toast.error("Gagal simpan."); } finally { setSaving(false); }
  };
  if (!b) return null;
  const r = b.readiness || {};
  return (
    <div className="rounded-2xl p-4" style={{ background: INK2 }} data-testid="b2m-export-panel">
      <p className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><Target className="h-3.5 w-3.5" /> Kesediaan Eksport {b.primary_market ? `· ${b.primary_market}` : ""}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: "#9AA6C2" }}>Skor</span>
        <span className="font-heading text-2xl font-bold text-white">{r.overall ?? "—"}<span className="text-sm" style={{ color: "#9AA6C2" }}>/100</span></span>
      </div>
      {r.status && <p className="text-xs font-bold" style={{ color: TEAL }}>{r.status.label}</p>}
      <div className="mt-2 space-y-1">
        {(r.subscores || []).map((s) => (
          <div key={s.key} className="flex items-center justify-between text-[11px]">
            <span className="text-white">{s.label}</span>
            <span className="rounded-full px-2 py-0.5 font-bold" style={{ background: `${GAP_COLOR[s.gap]}22`, color: GAP_COLOR[s.gap] }}>{s.score ?? "?"} · {s.gap}</span>
          </div>
        ))}
      </div>
      {(r.priority_actions || []).length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] font-bold" style={{ color: GOLD }}>Tindakan keutamaan:</p>
          <ol className="mt-1 list-decimal pl-4 text-[11px]" style={{ color: "#C7CEE0" }}>{r.priority_actions.map((a, i) => <li key={i}>{a}</li>)}</ol>
        </div>
      )}
      {(b.requirements || []).length > 0 && (
        <div className="mt-2" data-testid="b2m-export-reqs">
          <p className="text-[11px] font-bold" style={{ color: GOLD }}>Keperluan {b.primary_market}:</p>
          {b.requirements.map((q) => (
            <div key={q.id} className="mt-1 text-[11px]" style={{ color: "#C7CEE0" }}>
              <span className="rounded px-1.5 py-0.5 font-bold" style={{ background: `${REQ_COLOR[q.req_type]}22`, color: REQ_COLOR[q.req_type] }}>{q.req_type}</span> {q.name}
              {q.authority ? ` · ${q.authority}` : ""}{q.last_verified ? ` · disemak ${q.last_verified}` : ""}
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[10px] italic" style={{ color: "#6B769A" }}>{b.disclaimer}</p>
      <div className="mt-3 border-t pt-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <p className="mb-1 text-[11px] font-bold" style={{ color: "#9AA6C2" }}>Nilai kendiri (1-5):</p>
        {fields.map((f) => (
          <div key={f.id} className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] text-white">{f.label}</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((v) => (
                <button key={v} data-testid={`b2m-exp-${f.id}-${v}`} onClick={() => setRatings({ ...ratings, [f.id]: v })}
                  className="h-6 w-6 rounded-full text-[10px] font-bold" style={ratings[f.id] === v ? { background: GOLD, color: INK } : { background: "rgba(255,255,255,0.06)", color: "#9AA6C2" }}>{v}</button>
              ))}
            </div>
          </div>
        ))}
        <button data-testid="b2m-export-save" onClick={save} disabled={saving} className="mt-1 rounded-full px-4 py-2 text-xs font-bold" style={{ background: GOLD, color: INK }}>{saving ? "Mengira…" : "Kira Kesediaan Eksport"}</button>
      </div>
    </div>
  );
}

function RequirementsManager({ token, onBack }) {
  const [reqs, setReqs] = useState(null);
  const [meta, setMeta] = useState({ req_types: [], markets: [], sectors: [] });
  const [f, setF] = useState({ name: "", country: "", product_category: "", req_type: "To Verify", authority: "", source_url: "", last_verified: "", description: "" });
  const load = useCallback(() => { b2mListReqs(token).then((r) => setReqs(r.requirements)).catch(() => {}); }, [token]);
  useEffect(() => { load(); b2mExportMeta(token).then(setMeta).catch(() => {}); }, [load, token]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.name.trim() || !f.country.trim()) { toast.error("Nama & negara diperlukan."); return; }
    try { await b2mCreateReq(f, token); toast.success("Keperluan ditambah."); setF({ ...f, name: "", authority: "", source_url: "", last_verified: "", description: "" }); load(); }
    catch { toast.error("Gagal menambah."); }
  };
  const del = async (rid) => { try { await b2mDeleteReq(rid, token); load(); } catch { toast.error("Gagal padam."); } };
  return (
    <div className="mx-auto max-w-4xl px-6 py-8" data-testid="b2m-reqs-manager">
      <button onClick={onBack} className="flex items-center gap-1 text-sm" style={{ color: "#9AA6C2" }}><ArrowLeft className="h-4 w-4" /> Kembali</button>
      <h1 className="mt-4 font-heading text-2xl font-bold text-white">Pangkalan Keperluan Eksport</h1>
      <p className="text-xs" style={{ color: "#9AA6C2" }}>{meta.disclaimer}</p>
      <div className="mt-5 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="b2m-req-name" className="rounded-lg px-3 py-2 text-sm text-white outline-none sm:col-span-2" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Nama keperluan" value={f.name} onChange={set("name")} />
          <select data-testid="b2m-req-country" className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.country} onChange={set("country")}>
            <option value="" style={{ color: "#000" }}>Negara…</option>{(meta.markets || []).map((m) => <option key={m} value={m} style={{ color: "#000" }}>{m}</option>)}</select>
          <select data-testid="b2m-req-category" className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.product_category} onChange={set("product_category")}>
            <option value="" style={{ color: "#000" }}>Semua kategori</option>{(meta.sectors || []).map((s) => <option key={s} value={s} style={{ color: "#000" }}>{s}</option>)}</select>
          <select data-testid="b2m-req-type" className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.req_type} onChange={set("req_type")}>
            {(meta.req_types || []).map((t) => <option key={t} value={t} style={{ color: "#000" }}>{t}</option>)}</select>
          <input className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Pihak berkuasa" value={f.authority} onChange={set("authority")} />
          <input className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Sumber (URL)" value={f.source_url} onChange={set("source_url")} />
          <input type="date" className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.last_verified} onChange={set("last_verified")} />
        </div>
        <button data-testid="b2m-req-create" onClick={create} className="mt-3 rounded-full px-5 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}>Tambah Keperluan</button>
      </div>
      <div className="mt-5 space-y-2" data-testid="b2m-reqs-list">
        {reqs === null ? <FullLoader /> : reqs.length === 0 ? <p className="text-sm" style={{ color: "#9AA6C2" }}>Belum ada keperluan.</p> : reqs.map((q) => (
          <div key={q.id} className="flex items-center justify-between rounded-xl p-3" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`b2m-req-${q.id}`}>
            <div>
              <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ background: `${REQ_COLOR[q.req_type]}22`, color: REQ_COLOR[q.req_type] }}>{q.req_type}</span>
              <span className="ml-2 text-sm font-semibold text-white">{q.name}</span>
              <p className="text-[11px]" style={{ color: "#9AA6C2" }}>{q.country}{q.product_category ? ` · ${q.product_category}` : " · semua kategori"}{q.authority ? ` · ${q.authority}` : ""}{q.last_verified ? ` · disemak ${q.last_verified}` : ""}</p>
            </div>
            <button data-testid={`b2m-req-del-${q.id}`} onClick={() => del(q.id)} className="rounded-full p-2" style={{ background: "rgba(217,112,90,0.15)" }}><X className="h-3.5 w-3.5" style={{ color: "#D9705A" }} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CertVault({ pid, token }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ name: "", cert_type: "Halal", authority: "", issue_date: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { b2mCertList(pid, token).then(setData).catch(() => {}); }, [pid, token]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Sila pilih fail (PDF/JPG/PNG)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      await b2mCertUpload(pid, fd, token);
      toast.success("Sijil dimuat naik.");
      setF({ name: "", cert_type: "Halal", authority: "", issue_date: "", expiry_date: "" });
      setFile(null); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat naik."); }
    finally { setBusy(false); }
  };
  const setStatus = async (cid, status) => {
    try { await b2mCertUpdate(pid, cid, { verification_status: status }, token); load(); }
    catch { toast.error("Gagal kemas kini status."); }
  };
  const del = async (cid) => {
    if (!window.confirm("Padam sijil ini?")) return;
    try { await b2mCertDelete(pid, cid, token); load(); } catch { toast.error("Gagal padam."); }
  };
  const view = async (cid) => {
    try {
      const blob = await b2mCertFileBlob(pid, cid, token);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Gagal buka fail."); }
  };

  if (!data) return null;
  const certs = data.certifications || [];
  const types = data.cert_types || [];
  return (
    <div className="rounded-2xl p-4" style={{ background: INK2 }} data-testid="b2m-cert-vault">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><ShieldCheck className="h-3.5 w-3.5" /> Peti Pensijilan</p>
        <button data-testid="b2m-cert-add-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}>
          <FileUp className="h-3 w-3" /> {open ? "Tutup" : "Muat Naik"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2" data-testid="b2m-cert-form">
          <input data-testid="b2m-cert-name" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Nama sijil (cth: Sijil Halal JAKIM)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="b2m-cert-type" className="rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.cert_type} onChange={(e) => setF({ ...f, cert_type: e.target.value })}>
              {types.map((t) => <option key={t} value={t} style={{ color: "#000" }}>{t}</option>)}
            </select>
            <input className="rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Pihak berkuasa" value={f.authority} onChange={(e) => setF({ ...f, authority: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px]" style={{ color: "#9AA6C2" }}>Tarikh keluar</label>
              <input type="date" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} /></div>
            <div><label className="text-[10px]" style={{ color: "#9AA6C2" }}>Tarikh luput</label>
              <input type="date" data-testid="b2m-cert-expiry" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} /></div>
          </div>
          <input type="file" data-testid="b2m-cert-file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-xs text-white" />
          <p className="text-[10px]" style={{ color: "#6B769A" }}>PDF, JPG atau PNG · maks 10MB.</p>
          <button data-testid="b2m-cert-upload" onClick={upload} disabled={busy} className="rounded-full px-4 py-2 text-xs font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>{busy ? "Memuat naik…" : "Simpan Sijil"}</button>
        </div>
      )}

      <div className="mt-3 space-y-2" data-testid="b2m-cert-list">
        {certs.length === 0 ? <p className="text-[11px]" style={{ color: "#9AA6C2" }}>Belum ada sijil dimuat naik.</p> : certs.map((c) => {
          const em = EXPIRY_META[c.expiry?.bucket] || EXPIRY_META.none;
          return (
            <div key={c.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`b2m-cert-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-white"><FileText className="h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} /> <span className="truncate">{c.name}</span></p>
                  <p className="text-[11px]" style={{ color: "#9AA6C2" }}>{c.cert_type}{c.authority ? ` · ${c.authority}` : ""}{c.uploaded_by === "participant" ? " · oleh peserta" : ""}</p>
                </div>
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${CERT_STATUS_COLOR[c.verification_status]}22`, color: CERT_STATUS_COLOR[c.verification_status] }}>{c.verification_status}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-[10px]" style={{ color: em.color }}>
                <Clock className="h-3 w-3" /> {expiryText(c.expiry)}{c.expiry_date ? ` · luput ${c.expiry_date}` : ""}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button data-testid={`b2m-cert-view-${c.id}`} onClick={() => view(c.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.08)", color: "#C7CEE0" }}><Eye className="h-3 w-3" /> Lihat</button>
                <button data-testid={`b2m-cert-verify-${c.id}`} onClick={() => setStatus(c.id, "Disahkan")} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(47,168,159,0.15)", color: TEAL }}><ShieldCheck className="h-3 w-3" /> Sahkan</button>
                <button data-testid={`b2m-cert-reject-${c.id}`} onClick={() => setStatus(c.id, "Ditolak")} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(217,112,90,0.15)", color: "#D9705A" }}><ShieldAlert className="h-3 w-3" /> Tolak</button>
                <button data-testid={`b2m-cert-del-${c.id}`} onClick={() => del(c.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.06)", color: "#8A93B2" }}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EvidenceVault({ pid, token }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ title: "", evidence_type: "Invois", description: "", amount: "", evidence_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { b2mEviList(pid, token).then(setData).catch(() => {}); }, [pid, token]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Sila pilih fail (PDF/JPG/PNG)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      await b2mEviUpload(pid, fd, token);
      toast.success("Bukti dimuat naik.");
      setF({ title: "", evidence_type: "Invois", description: "", amount: "", evidence_date: "" });
      setFile(null); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat naik."); }
    finally { setBusy(false); }
  };
  const setStatus = async (eid, status) => {
    try { await b2mEviUpdate(pid, eid, { review_status: status }, token); load(); }
    catch { toast.error("Gagal kemas kini."); }
  };
  const del = async (eid) => {
    if (!window.confirm("Padam bukti ini?")) return;
    try { await b2mEviDelete(pid, eid, token); load(); } catch { toast.error("Gagal padam."); }
  };
  const view = async (eid) => {
    try { const blob = await b2mEviFileBlob(pid, eid, token); const url = URL.createObjectURL(blob); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Gagal buka fail."); }
  };

  if (!data) return null;
  const items = data.evidence || [];
  const types = data.evidence_types || [];
  return (
    <div className="rounded-2xl p-4" style={{ background: INK2 }} data-testid="b2m-evidence-vault">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><Receipt className="h-3.5 w-3.5" /> Peti Bukti Komersial</p>
        <button data-testid="b2m-evi-add-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}>
          <FileUp className="h-3 w-3" /> {open ? "Tutup" : "Muat Naik"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2" data-testid="b2m-evi-form">
          <input data-testid="b2m-evi-title" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Tajuk (cth: Invois eksport Singapura)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="b2m-evi-type" className="rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.evidence_type} onChange={(e) => setF({ ...f, evidence_type: e.target.value })}>
              {types.map((t) => <option key={t} value={t} style={{ color: "#000" }}>{t}</option>)}
            </select>
            <input className="rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Nilai (cth: RM12,500)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px]" style={{ color: "#9AA6C2" }}>Tarikh</label>
              <input type="date" data-testid="b2m-evi-date" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.evidence_date} onChange={(e) => setF({ ...f, evidence_date: e.target.value })} /></div>
            <input className="mt-4 rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Nota ringkas" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <input type="file" data-testid="b2m-evi-file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-xs text-white" />
          <p className="text-[10px]" style={{ color: "#6B769A" }}>PDF, JPG atau PNG · maks 10MB.</p>
          <button data-testid="b2m-evi-upload" onClick={upload} disabled={busy} className="rounded-full px-4 py-2 text-xs font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>{busy ? "Memuat naik…" : "Simpan Bukti"}</button>
        </div>
      )}

      <div className="mt-3 space-y-2" data-testid="b2m-evi-list">
        {items.length === 0 ? <p className="text-[11px]" style={{ color: "#9AA6C2" }}>Belum ada bukti dimuat naik.</p> : items.map((e) => (
          <div key={e.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`b2m-evi-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-white"><Receipt className="h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} /> <span className="truncate">{e.title}</span></p>
                <p className="text-[11px]" style={{ color: "#9AA6C2" }}>{e.evidence_type}{e.amount ? ` · ${e.amount}` : ""}{e.evidence_date ? ` · ${e.evidence_date}` : ""}{e.uploaded_by === "participant" ? " · oleh peserta" : ""}</p>
              </div>
              <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${EVI_STATUS_COLOR[e.review_status]}22`, color: EVI_STATUS_COLOR[e.review_status] }}>{e.review_status}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button data-testid={`b2m-evi-view-${e.id}`} onClick={() => view(e.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.08)", color: "#C7CEE0" }}><Eye className="h-3 w-3" /> Lihat</button>
              <button data-testid={`b2m-evi-review-${e.id}`} onClick={() => setStatus(e.id, "Disemak")} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(107,143,214,0.15)", color: "#8FB0EA" }}><Eye className="h-3 w-3" /> Disemak</button>
              <button data-testid={`b2m-evi-verify-${e.id}`} onClick={() => setStatus(e.id, "Disahkan")} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(47,168,159,0.15)", color: TEAL }}><BadgeCheck className="h-3 w-3" /> Sahkan</button>
              <button data-testid={`b2m-evi-del-${e.id}`} onClick={() => del(e.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.06)", color: "#8A93B2" }}><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ParticipantDrawer({ pid, token, onClose }) {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState({ observation: "", recommended_intervention: "", action_agreed: "", next_review: "", private_note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    b2mParticipantDetail(pid, token).then((r) => { setD(r); setNotes({ observation: "", recommended_intervention: "", action_agreed: "", next_review: "", private_note: "", ...(r.consultant_notes || {}) }); }).catch(() => toast.error("Gagal memuatkan."));
  }, [pid, token]);
  const save = async () => { setSaving(true); try { await b2mSaveNotes(pid, notes, token); toast.success("Nota disimpan."); } catch { toast.error("Gagal simpan."); } finally { setSaving(false); } };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="b2m-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="h-full w-full max-w-md overflow-y-auto p-6" style={{ background: INK }}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">Butiran Peserta</h2>
          <button data-testid="b2m-drawer-close" onClick={onClose} className="rounded-full p-2" style={{ background: INK2 }}><X className="h-4 w-4 text-white" /></button>
        </div>
        {!d ? <FullLoader /> : (
          <div className="mt-4 space-y-4">
            <div>
              <p className="font-heading text-xl font-bold text-white">{d.name}</p>
              <p className="text-sm" style={{ color: "#9AA6C2" }}>{d.business_name} · {d.sector}</p>
              {d.primary_target_market && <p className="text-xs" style={{ color: TEAL }}>Sasaran: {d.primary_target_market}</p>}
            </div>
            {d.b2m_score != null && (
              <div className="rounded-2xl p-4" style={{ background: INK2 }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider" style={{ color: "#9AA6C2" }}>Skor B2M (PRE)</span>
                  <span className="font-heading text-2xl font-bold text-white">{d.b2m_score}</span>
                </div>
                {d.improvement && <p className="mt-1 text-xs" style={{ color: TEAL }}>Peningkatan PRE→POST: +{d.improvement.overall}</p>}
                <p className="mt-1 text-xs" style={{ color: GOLD }}>Kekangan utama: {d.primary_constraint}</p>
              </div>
            )}
            {d.pre_scores && (
              <div className="space-y-1.5">
                {Object.entries(d.stage_labels).map(([k, lbl]) => {
                  const pre = d.pre_scores.stage_scores?.[k]; const post = d.post_scores?.stage_scores?.[k];
                  return (
                    <div key={k} className="flex items-center gap-2 text-xs">
                      <span className="w-24 text-white">{lbl}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.1)" }}>
                        <div className="h-full rounded-full" style={{ width: `${pre || 0}%`, background: GOLD }} />
                      </div>
                      <span className="w-16 text-right text-white">{pre ?? "—"}{post != null ? ` → ${post}` : ""}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-1.5">
              {CHECKPOINTS.map(([k, lbl]) => (
                <span key={k} className="rounded-full px-2 py-1 text-[10px] font-bold"
                  style={d.checkpoint_status?.[k] ? { background: "rgba(47,168,159,0.2)", color: TEAL } : { background: "rgba(255,255,255,0.06)", color: "#6B769A" }}>{lbl}</span>
              ))}
            </div>
            <button data-testid="b2m-individual-report" onClick={async () => {
              try { const b = await b2mIndividualReport(pid, token); downloadBlob(b, `b2m-progress-${pid.slice(0, 8)}.pdf`); }
              catch { toast.error("Gagal jana laporan."); }
            }} className="flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold" style={{ background: GOLD, color: INK }}>
              <Download className="h-4 w-4" /> Laporan Kemajuan Individu (PDF)
            </button>
            <button data-testid="b2m-success-story" onClick={async () => {
              try { const b = await b2mSuccessStory(pid, token); downloadBlob(b, `success-story-${pid.slice(0, 8)}.pdf`); }
              catch { toast.error("Gagal jana kisah kejayaan."); }
            }} className="flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold" style={{ background: TEAL, color: INK }}>
              <Star className="h-4 w-4" /> Kad Kisah Kejayaan (PDF)
            </button>
            <ExportPanel pid={pid} token={token} sector={d.sector} market={d.primary_target_market} />
            <CertVault pid={pid} token={token} />
            <EvidenceVault pid={pid} token={token} />
            <div className="rounded-2xl p-4" style={{ background: INK2 }} data-testid="b2m-notes-editor">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}>Nota Konsultan</p>
              {[["observation", "Pemerhatian"], ["recommended_intervention", "Intervensi disyorkan"], ["action_agreed", "Tindakan dipersetujui"], ["private_note", "Nota peribadi (tidak dikongsi)"]].map(([k, lbl]) => (
                <div key={k} className="mb-2">
                  <label className="text-[11px]" style={{ color: "#9AA6C2" }}>{lbl}</label>
                  <textarea data-testid={`b2m-note-${k}`} rows={2} value={notes[k]} onChange={(e) => setNotes({ ...notes, [k]: e.target.value })}
                    className="mt-1 w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} />
                </div>
              ))}
              <button data-testid="b2m-note-save" onClick={save} disabled={saving} className="mt-1 rounded-full px-4 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}>{saving ? "Menyimpan…" : "Simpan Nota"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CohortDetail({ code, token, onBack }) {
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState({ sectors: [], markets: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [csv, setCsv] = useState("name,email,phone,business_name,sector,product_category,primary_target_market\n");
  const [copied, setCopied] = useState(false);
  const [drawerPid, setDrawerPid] = useState(null);
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState("");
  const [orgLink, setOrgLink] = useState("");
  const [busyReport, setBusyReport] = useState("");
  const [editPart, setEditPart] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [p, setP] = useState({ name: "", email: "", phone: "", business_name: "", sector: "Other", product_category: "", primary_target_market: "" });

  const load = useCallback(() => { b2mCohortDetail(code, token).then(setD).catch(() => toast.error("Gagal memuatkan.")); }, [code, token]);
  useEffect(() => { load(); b2mMeta().then(setMeta).catch(() => {}); }, [load]);

  const sendCp = async (cp) => {
    setSending(cp);
    try { const r = await b2mSendCheckpoint(code, cp, token); toast.success(`${cp.toUpperCase()}: ${r.sent} dihantar, ${r.skipped} dilangkau${r.failed ? `, ${r.failed} gagal` : ""}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghantar."); }
    finally { setSending(""); }
  };

  const addPart = async () => {
    if (!p.name.trim()) { toast.error("Nama peserta diperlukan."); return; }
    try { await b2mAddParticipant(code, { ...p, target_markets: p.primary_target_market ? [p.primary_target_market] : [] }, token); toast.success("Peserta ditambah."); setP({ name: "", email: "", phone: "", business_name: "", sector: "Other", product_category: "", primary_target_market: "" }); setShowAdd(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah peserta."); }
  };
  const importCsv = async () => {
    try { const r = await b2mImportParticipants(code, csv, token); toast.success(`${r.added} peserta diimport.`); setShowCsv(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal import."); }
  };
  const copyCode = async () => { try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {} };

  if (!d) return <FullLoader />;
  const c = d.counts || {};
  return (
    <div className="mx-auto max-w-5xl px-6 py-8" data-testid="b2m-cohort-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm" style={{ color: "#9AA6C2" }}><ArrowLeft className="h-4 w-4" /> Semua kohort</button>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-bold text-white">{d.cohort_name}</h1>
            {d.is_demo && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#B4530022", color: "#F0A868" }}>DEMO</span>}
          </div>
          <p className="text-sm" style={{ color: "#9AA6C2" }}><Building2 className="mr-1 inline h-3.5 w-3.5" />{d.sponsoring_org}</p>
        </div>
        <button data-testid="b2m-copy-code" onClick={copyCode} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: INK2, color: GOLD, border: "1px solid rgba(214,162,90,0.3)" }}>
          {copied ? <CheckCircle2 className="h-4 w-4" style={{ color: TEAL }} /> : <Copy className="h-4 w-4" />} {d.access_code}
        </button>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-7">
        <Stat label="Dijemput" value={c.invited || 0} />
        <Stat label="Daftar" value={c.registered || 0} />
        <Stat label="PRE" value={c.pre || 0} />
        <Stat label="POST" value={c.post || 0} />
        <Stat label="Hari 30" value={c.day30 || 0} />
        <Stat label="Hari 60" value={c.day60 || 0} />
        <Stat label="Hari 90" value={c.day90 || 0} />
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="b2m-add-participant-btn" onClick={() => { setShowAdd(!showAdd); setShowCsv(false); }} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}><Plus className="h-4 w-4" /> Tambah Peserta</button>
        <button data-testid="b2m-import-csv-btn" onClick={() => { setShowCsv(!showCsv); setShowAdd(false); }} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.15)" }}><Upload className="h-4 w-4" /> Import CSV</button>
      </div>

      <div className="mt-4 rounded-2xl p-4" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-send-panel">
        <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><Send className="h-3.5 w-3.5" /> Hantar jemputan penilaian (emel + pautan selamat)</p>
        <div className="flex flex-wrap gap-2">
          {CHECKPOINTS.map(([k, lbl]) => (
            <button key={k} data-testid={`b2m-send-${k}`} onClick={() => sendCp(k)} disabled={sending === k}
              className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold disabled:opacity-50"
              style={{ background: "rgba(214,162,90,0.12)", color: GOLD, border: "1px solid rgba(214,162,90,0.3)" }}>
              {sending === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-2xl p-4" style={{ background: "linear-gradient(135deg,#12233A,#1E2647)", border: "1px solid rgba(47,168,159,0.25)" }} data-testid="b2m-report-panel">
        <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}><FileText className="h-3.5 w-3.5" /> Laporan Impak Organisasi</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="b2m-review-impact" onClick={() => setShowReview(true)}
            className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold" style={{ background: "rgba(47,168,159,0.18)", color: TEAL, border: "1px solid rgba(47,168,159,0.4)" }}>
            <ClipboardCheck className="h-3.5 w-3.5" /> Semak & Sunting</button>
          <button data-testid="b2m-org-pdf" disabled={busyReport === "pdf"} onClick={async () => { setBusyReport("pdf"); try { const b = await b2mOrgReportPdf(code, token); downloadBlob(b, `b2m-impact-${code}.pdf`); } catch { toast.error("Gagal jana."); } finally { setBusyReport(""); } }}
            className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold disabled:opacity-50" style={{ background: GOLD, color: INK }}>{busyReport === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF Impak</button>
          <button data-testid="b2m-org-csv" onClick={async () => { try { const b = await b2mOrgCsv(code, token); downloadBlob(b, `b2m-impact-${code}.csv`); } catch { toast.error("Gagal."); } }}
            className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold text-white" style={{ border: "1px solid rgba(255,255,255,0.15)" }}><FileSpreadsheet className="h-3.5 w-3.5" style={{ color: TEAL }} /> CSV</button>
          <button data-testid="b2m-org-xlsx" onClick={async () => { try { const b = await b2mOrgXlsx(code, token); downloadBlob(b, `b2m-impact-${code}.xlsx`); } catch { toast.error("Gagal."); } }}
            className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold text-white" style={{ border: "1px solid rgba(255,255,255,0.15)" }}><FileSpreadsheet className="h-3.5 w-3.5" style={{ color: TEAL }} /> XLSX</button>
          <button data-testid="b2m-org-link" onClick={async () => { try { const r = await b2mOrgLinkIssue(code, token); setOrgLink(r.link); await navigator.clipboard.writeText(r.link).catch(() => {}); toast.success("Pautan baca-sahaja dijana & disalin."); } catch { toast.error("Gagal jana pautan."); } }}
            className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold text-white" style={{ border: "1px solid rgba(214,162,90,0.35)" }}><Link2 className="h-3.5 w-3.5" style={{ color: GOLD }} /> Pautan Organisasi (Baca-Sahaja)</button>
        </div>
        {orgLink && <p className="mt-2 break-all text-[11px]" style={{ color: "#9AA6C2" }} data-testid="b2m-org-link-value">{orgLink}</p>}
        <p className="mt-1.5 text-[10px]" style={{ color: "#6B769A" }}>Pautan organisasi memaparkan laporan agregat sahaja — tanpa nota peribadi atau fail peserta.</p>
      </div>

      {showAdd && (
        <div className="mt-4 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-add-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="b2m-p-name" className={field} style={fieldStyle} placeholder="Nama" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
            <input data-testid="b2m-p-email" className={field} style={fieldStyle} placeholder="Emel" value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} />
            <input className={field} style={fieldStyle} placeholder="Telefon" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} />
            <input data-testid="b2m-p-biz" className={field} style={fieldStyle} placeholder="Nama Bisnes" value={p.business_name} onChange={(e) => setP({ ...p, business_name: e.target.value })} />
            <select data-testid="b2m-p-sector" className={field} style={fieldStyle} value={p.sector} onChange={(e) => setP({ ...p, sector: e.target.value })}>
              {(meta.sectors || []).map((s) => <option key={s} value={s} style={{ color: "#000" }}>{s}</option>)}
            </select>
            <input className={field} style={fieldStyle} placeholder="Kategori produk/servis" value={p.product_category} onChange={(e) => setP({ ...p, product_category: e.target.value })} />
            <select data-testid="b2m-p-market" className={field} style={fieldStyle} value={p.primary_target_market} onChange={(e) => setP({ ...p, primary_target_market: e.target.value })}>
              <option value="" style={{ color: "#000" }}>Pasaran sasaran utama…</option>
              {(meta.markets || []).map((m) => <option key={m} value={m} style={{ color: "#000" }}>{m}</option>)}
            </select>
          </div>
          <button data-testid="b2m-p-submit" onClick={addPart} className="mt-4 rounded-full px-5 py-2.5 text-sm font-bold" style={{ background: GOLD, color: INK }}>Simpan Peserta</button>
        </div>
      )}
      {showCsv && (
        <div className="mt-4 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-csv-form">
          <p className="mb-2 text-xs" style={{ color: "#9AA6C2" }}>Tampal CSV (baris pertama: pengepala).</p>
          <textarea data-testid="b2m-csv-input" rows={6} className={`${field} font-mono text-xs`} style={fieldStyle} value={csv} onChange={(e) => setCsv(e.target.value)} />
          <button data-testid="b2m-csv-submit" onClick={importCsv} className="mt-3 rounded-full px-5 py-2.5 text-sm font-bold" style={{ background: GOLD, color: INK }}>Import</button>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-2xl" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-participant-list">
        {(d.participants || []).length === 0 ? (
          <p className="p-6 text-sm" style={{ color: "#9AA6C2" }} data-testid="b2m-no-participants">Belum ada peserta. Tambah peserta atau import CSV.</p>
        ) : (
          <>
            <div className="p-3">
              <input data-testid="b2m-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari nama / bisnes / sektor…"
                className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} />
            </div>
            <table className="w-full text-left text-sm">
              <thead><tr style={{ color: "#9AA6C2" }} className="text-[11px] uppercase tracking-wide">
                <th className="p-3">Nama</th><th className="p-3">Sektor</th><th className="p-3">Skor</th><th className="p-3">Kekangan</th><th className="p-3">Checkpoint</th><th className="p-3">Tindakan</th>
              </tr></thead>
              <tbody>
                {d.participants.filter((pt) => {
                  const q = query.trim().toLowerCase();
                  return !q || [pt.name, pt.business_name, pt.sector, pt.primary_target_market].some((x) => (x || "").toLowerCase().includes(q));
                }).map((pt) => (
                  <tr key={pt.id} onClick={() => setDrawerPid(pt.id)} className="cursor-pointer border-t text-white hover:bg-white/5" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid={`b2m-part-row-${pt.id}`}>
                    <td className="p-3"><span className="font-semibold">{pt.name}</span><br /><span className="text-xs" style={{ color: "#9AA6C2" }}>{pt.business_name || "—"}</span></td>
                    <td className="p-3 text-xs" style={{ color: "#C7CEE0" }}>{pt.sector}{pt.is_service ? " (servis)" : ""}</td>
                    <td className="p-3 font-bold" style={{ color: GOLD }}>{pt.b2m_score ?? "—"}</td>
                    <td className="p-3 text-xs" style={{ color: "#C7CEE0" }}>{pt.primary_constraint || "—"}</td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {CHECKPOINTS.map(([k, lbl]) => (
                          <span key={k} className="rounded px-1.5 py-0.5 text-[9px] font-bold" style={pt.checkpoint_status?.[k] ? { background: "rgba(47,168,159,0.2)", color: TEAL } : { background: "rgba(255,255,255,0.05)", color: "#5B6688" }}>{lbl}</span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1.5">
                        <button data-testid={`b2m-edit-part-${pt.id}`} onClick={(e) => { e.stopPropagation(); setEditPart(pt); }}
                          className="rounded-full p-1.5" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }} title="Sunting"><Pencil className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      {drawerPid && <ParticipantDrawer pid={drawerPid} token={token} onClose={() => { setDrawerPid(null); load(); }} />}
      {editPart && <ParticipantEditModal kind="b2m" participant={editPart} meta={meta} token={token} onClose={(reload) => { setEditPart(null); if (reload) load(); }} />}
      {showReview && <ImpactReviewModal code={code} token={token} onClose={() => setShowReview(false)} />}
    </div>
  );
}

function JourneyStepper({ journey }) {
  if (!journey) return null;
  return (
    <div className="rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="bg-journey">
      <p className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>
        <Workflow className="h-3.5 w-3.5" /> Perjalanan Program Kohort
      </p>
      <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
        {journey.stages.map((s, i) => {
          const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
          const col = s.complete ? TEAL : s.done > 0 ? GOLD : "#5B6688";
          return (
            <React.Fragment key={s.key}>
              <div className="flex-1 rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${s.complete ? "rgba(47,168,159,0.4)" : "rgba(255,255,255,0.08)"}` }} data-testid={`bg-journey-${s.key}`}>
                <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold" style={{ background: s.complete ? "rgba(47,168,159,0.2)" : "rgba(214,162,90,0.12)", color: col }}>
                  {s.complete ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <p className="mt-2 text-[11px] font-bold tracking-wide text-white">{s.label}</p>
                <p className="mt-0.5 text-[10px] leading-tight" style={{ color: "#9AA6C2" }}>{s.sub}</p>
                <p className="mt-1.5 text-[11px] font-bold" style={{ color: col }}>{s.done}/{s.total} · {pct}%</p>
              </div>
              {i < journey.stages.length - 1 && (
                <div className="hidden items-center md:flex"><ChevronRight className="h-4 w-4" style={{ color: "#5B6688" }} /></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function BGAggregate({ agg }) {
  if (!agg) return null;
  return (
    <div className="rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="bg-aggregate">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}><Target className="h-3.5 w-3.5" /> Diagnosis Agregat Business Growth</p>
        <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ background: "rgba(255,255,255,0.06)", color: "#C7CEE0" }}>n={agg.n}</span>
      </div>
      {agg.n === 0 ? (
        <p className="mt-3 text-sm" style={{ color: "#9AA6C2" }}>Belum ada peserta yang melengkapkan diagnostik.</p>
      ) : (
        <>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-heading text-3xl font-bold text-white">{agg.overall_avg}</span>
            <span className="text-xs" style={{ color: "#9AA6C2" }}>purata skor keseluruhan kohort</span>
          </div>
          <div className="mt-4 space-y-2">
            {Object.entries(agg.factor_avg).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="w-24 text-white">{agg.labels[k]}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.1)" }}>
                  <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 65 ? TEAL : v >= 50 ? GOLD : "#D9705A" }} />
                </div>
                <span className="w-10 text-right font-bold text-white">{v}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs" style={{ color: GOLD }}>Kekangan paling lazim: <b>{agg.common_blocker || "—"}</b></p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(agg.distribution).map(([lbl, n]) => (
              <span key={lbl} className="rounded-full px-2.5 py-0.5 text-[10px] font-bold" style={{ background: "rgba(214,162,90,0.12)", color: "#E8C98A" }}>{lbl}: {n}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BGCohortDetail({ code, token, onBack, onOpenB2M }) {
  const [d, setD] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bridging, setBridging] = useState(false);
  const [sending, setSending] = useState(false);
  const [editPart, setEditPart] = useState(null);
  const [p, setP] = useState({ name: "", email: "", phone: "", business_name: "", industri: "" });

  const load = useCallback(() => { bgCohortDetail(code, token).then(setD).catch(() => toast.error("Gagal memuatkan.")); }, [code, token]);
  useEffect(() => { load(); bgMeta().then((r) => setIndustries(r.industries || [])).catch(() => {}); }, [load]);

  const addPart = async () => {
    if (!p.name.trim()) { toast.error("Nama peserta diperlukan."); return; }
    try { await bgAddParticipant(code, p, token); toast.success("Peserta ditambah."); setP({ name: "", email: "", phone: "", business_name: "", industri: "" }); setShowAdd(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah."); }
  };
  const bridge = async () => {
    setBridging(true);
    try { const r = await bgBridge(code, token); toast.success(`Diteruskan ke Brand2Market™ — ${r.bridged} peserta.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal."); }
    finally { setBridging(false); }
  };
  const sendAll = async () => {
    setSending(true);
    try { const r = await bgSendAll(code, token); toast.success(`${r.sent} jemputan dihantar, ${r.skipped} dilangkau.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghantar."); }
    finally { setSending(false); }
  };
  const del = async () => {
    if (!window.confirm("Padam kohort ini (termasuk kohort Brand2Market yang dijana)?")) return;
    try { await bgDeleteCohort(code, token); toast.success("Kohort dipadam."); onBack(); }
    catch { toast.error("Gagal padam."); }
  };
  const copyCode = async () => { try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch {} };

  if (!d) return <FullLoader />;
  const c = d.counts || {};
  return (
    <div className="mx-auto max-w-5xl px-6 py-8" data-testid="bg-cohort-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm" style={{ color: "#9AA6C2" }}><ArrowLeft className="h-4 w-4" /> Semua kohort Business Growth</button>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-bold text-white">{d.cohort_name}</h1>
            {d.is_demo && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#B4530022", color: "#F0A868" }}>DEMO</span>}
          </div>
          <p className="text-sm" style={{ color: "#9AA6C2" }}><Building2 className="mr-1 inline h-3.5 w-3.5" />{d.sponsoring_org} · {d.programme_name}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="bg-copy-code" onClick={copyCode} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: INK2, color: GOLD, border: "1px solid rgba(214,162,90,0.3)" }}>
            {copied ? <CheckCircle2 className="h-4 w-4" style={{ color: TEAL }} /> : <Copy className="h-4 w-4" />} {d.access_code}
          </button>
          <button data-testid="bg-delete-cohort" onClick={del} className="flex items-center gap-2 rounded-full px-3 py-2 text-sm" style={{ border: "1px solid rgba(217,112,90,0.4)", color: "#E8A87C" }}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <JourneyStepper journey={d.journey} />

        <div className="rounded-2xl p-4" style={{ background: "linear-gradient(135deg,#12233A,#1E2647)", border: "1px solid rgba(47,168,159,0.3)" }} data-testid="bg-bridge-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-bold text-white"><GitBranch className="h-4 w-4" style={{ color: TEAL }} /> Sambungan ke Brand2Market™</p>
              <p className="mt-0.5 text-xs" style={{ color: "#9AA6C2" }}>
                {d.journey?.bridged ? `Kohort ini telah diteruskan ke Brand2Market (${d.journey.b2m_code}). Skor PRE Brand2Market tetap perlu dilengkapkan.` : "Setelah diagnostik selesai, teruskan kohort ini ke program Brand2Market™."}
              </p>
            </div>
            {d.journey?.bridged ? (
              <button data-testid="bg-open-b2m" onClick={() => onOpenB2M(d.journey.b2m_code)} className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold" style={{ background: TEAL, color: INK }}>
                <ExternalLink className="h-3.5 w-3.5" /> Buka Kohort Brand2Market
              </button>
            ) : (
              <button data-testid="bg-bridge-btn" onClick={bridge} disabled={bridging} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-60" style={{ background: TEAL, color: INK }}>
                {bridging ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightIcon />} Teruskan Kohort ke Brand2Market™
              </button>
            )}
          </div>
        </div>

        <BGAggregate agg={d.aggregate} />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Dijemput" value={c.invited || 0} />
        <Stat label="Selesai Diagnostik" value={c.completed || 0} />
        <Stat label="Purata Skor" value={d.aggregate?.overall_avg ?? "—"} />
        <Stat label="Diteruskan B2M" value={d.journey?.bridged ? "Ya" : "Belum"} />
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="bg-add-participant-btn" onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}><Plus className="h-4 w-4" /> Tambah Peserta</button>
        <button data-testid="bg-send-all" onClick={sendAll} disabled={sending} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white disabled:opacity-50" style={{ background: INK2, border: "1px solid rgba(214,162,90,0.3)" }}>{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Hantar Jemputan Diagnostik</button>
      </div>

      {showAdd && (
        <div className="mt-4 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="bg-add-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="bg-p-name" className={field} style={fieldStyle} placeholder="Nama" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
            <input data-testid="bg-p-email" className={field} style={fieldStyle} placeholder="Emel" value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} />
            <input data-testid="bg-p-biz" className={field} style={fieldStyle} placeholder="Nama Bisnes" value={p.business_name} onChange={(e) => setP({ ...p, business_name: e.target.value })} />
            <select data-testid="bg-p-industri" className={field} style={fieldStyle} value={p.industri} onChange={(e) => setP({ ...p, industri: e.target.value })}>
              <option value="" style={{ color: "#000" }}>Pilih industri…</option>
              {industries.map((s) => <option key={s} value={s} style={{ color: "#000" }}>{s}</option>)}
            </select>
          </div>
          <button data-testid="bg-p-submit" onClick={addPart} className="mt-4 rounded-full px-5 py-2.5 text-sm font-bold" style={{ background: GOLD, color: INK }}>Simpan Peserta</button>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-2xl" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="bg-participant-list">
        {(d.participants || []).length === 0 ? (
          <p className="p-6 text-sm" style={{ color: "#9AA6C2" }}>Belum ada peserta.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead><tr style={{ color: "#9AA6C2" }} className="text-[11px] uppercase tracking-wide">
              <th className="p-3">Nama</th><th className="p-3">Industri</th><th className="p-3">Diagnostik</th><th className="p-3">Skor</th><th className="p-3">Laporan</th><th className="p-3">Tindakan</th>
            </tr></thead>
            <tbody>
              {d.participants.map((pt) => (
                <tr key={pt.id} className="border-t text-white" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid={`bg-part-row-${pt.id}`}>
                  <td className="p-3"><span className="font-semibold">{pt.name}</span><br /><span className="text-xs" style={{ color: "#9AA6C2" }}>{pt.business_name || "—"}</span></td>
                  <td className="p-3 text-xs" style={{ color: "#C7CEE0" }}>{pt.industri || "—"}</td>
                  <td className="p-3">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={pt.assessment_done ? { background: "rgba(47,168,159,0.2)", color: TEAL } : { background: "rgba(255,255,255,0.06)", color: "#8A93B2" }}>{pt.assessment_done ? "Selesai" : "Belum"}</span>
                  </td>
                  <td className="p-3 font-bold" style={{ color: GOLD }}>{pt.overall ?? "—"}</td>
                  <td className="p-3">
                    {pt.assessment_id ? (
                      <button data-testid={`bg-view-report-${pt.id}`} onClick={() => window.open(`/report/${pt.assessment_id}`, "_blank")} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}><FileText className="h-3 w-3" /> Lihat</button>
                    ) : <span className="text-xs" style={{ color: "#5B6688" }}>—</span>}
                  </td>
                  <td className="p-3">
                    <button data-testid={`bg-edit-part-${pt.id}`} onClick={() => setEditPart(pt)}
                      className="rounded-full p-1.5" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }} title="Sunting"><Pencil className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editPart && <ParticipantEditModal kind="bg" participant={editPart} token={token} onClose={(reload) => { setEditPart(null); if (reload) load(); }} />}
    </div>
  );
}

function ArrowRightIcon() { return <ChevronRight className="h-4 w-4" />; }

function BGSection({ token, onOpenB2M }) {
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [form, setForm] = useState({ sponsoring_org: "", cohort_name: "", programme_name: "Business Growth Cohort" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { bgListCohorts(token).then((r) => setCohorts(r.cohorts)).catch(() => {}); }, [token]);
  useEffect(() => { if (view === "list") load(); }, [view, load]);

  const create = async () => {
    if (!form.sponsoring_org.trim() || !form.cohort_name.trim()) { toast.error("Organisasi & nama kohort diperlukan."); return; }
    setBusy(true);
    try { await bgCreateCohort(form, token); toast.success("Kohort dicipta."); setForm({ sponsoring_org: "", cohort_name: "", programme_name: "Business Growth Cohort" }); setView("list"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal cipta."); }
    finally { setBusy(false); }
  };
  const seedDemo = async () => { setBusy(true); try { await bgDemoSeed(token); toast.success("Kohort demo dijana semula."); load(); } catch { toast.error("Gagal jana demo."); } finally { setBusy(false); } };
  const delDemo = async () => { if (!window.confirm("Padam kohort demo?")) return; try { await bgDemoDelete(token); toast.success("Demo dipadam."); load(); } catch { toast.error("Gagal."); } };
  const dlAgency = async () => { setBusy(true); try { const b = await b2mAgencyPack(token); downloadBlob(b, "growth-factor-agency-demo-pack.pdf"); } catch { toast.error("Gagal jana pek. Pastikan kohort demo wujud."); } finally { setBusy(false); } };
  const dlEcosystem = async () => { setBusy(true); try { const b = await b2mEcosystemPng(token); downloadBlob(b, "growth-factor-ecosystem-overview.png"); } catch { toast.error("Gagal jana visual."); } finally { setBusy(false); } };

  if (selected) return <BGCohortDetail code={selected} token={token} onBack={() => { setSelected(null); load(); }} onOpenB2M={onOpenB2M} />;

  return (
    <div data-testid="bg-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-white"><Briefcase className="h-5 w-5" style={{ color: GOLD }} /> Kohort Business Growth</h2>
        <div className="flex flex-wrap gap-2">
          <button data-testid="bg-agency-pack" onClick={dlAgency} disabled={busy} className="flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-bold disabled:opacity-50" style={{ background: TEAL, color: INK }}><Download className="h-3.5 w-3.5" /> Pek Demo Agensi (PDF)</button>
          <button data-testid="bg-ecosystem-png" onClick={dlEcosystem} disabled={busy} className="flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-bold text-white disabled:opacity-50" style={{ border: "1px solid rgba(47,168,159,0.4)" }}><Download className="h-3.5 w-3.5" style={{ color: TEAL }} /> Visual Ekosistem (PNG)</button>
          <button data-testid="bg-seed-demo" onClick={seedDemo} disabled={busy} className="flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-bold text-white disabled:opacity-50" style={{ border: "1px solid rgba(214,162,90,0.35)" }}><Sparkles className="h-3.5 w-3.5" style={{ color: GOLD }} /> Jana Demo</button>
          <button data-testid="bg-del-demo" onClick={delDemo} className="flex items-center gap-2 rounded-full px-3.5 py-2 text-xs text-white" style={{ border: "1px solid rgba(217,112,90,0.35)", color: "#E8A87C" }}><Trash2 className="h-3.5 w-3.5" /> Padam Demo</button>
          <button data-testid="bg-create-cohort-btn" onClick={() => setView(view === "create" ? "list" : "create")} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}><Plus className="h-4 w-4" /> Cipta Kohort</button>
        </div>
      </div>

      {view === "create" && (
        <div className="mb-5 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="bg-create-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="bg-c-org" className={field} style={fieldStyle} placeholder="Organisasi penaja (cth: MARA)" value={form.sponsoring_org} onChange={(e) => setForm({ ...form, sponsoring_org: e.target.value })} />
            <input data-testid="bg-c-name" className={field} style={fieldStyle} placeholder="Nama kohort" value={form.cohort_name} onChange={(e) => setForm({ ...form, cohort_name: e.target.value })} />
            <input className={field} style={fieldStyle} placeholder="Nama program" value={form.programme_name} onChange={(e) => setForm({ ...form, programme_name: e.target.value })} />
          </div>
          <button data-testid="bg-c-submit" onClick={create} disabled={busy} className="mt-4 rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>{busy ? "Menyimpan…" : "Cipta Kohort"}</button>
        </div>
      )}

      {cohorts === null ? <FullLoader /> : cohorts.length === 0 ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="bg-no-cohorts">
          <Briefcase className="mx-auto h-8 w-8" style={{ color: GOLD }} />
          <p className="mt-3 text-sm" style={{ color: "#9AA6C2" }}>Belum ada kohort Business Growth. Cipta satu atau jana kohort demo.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {cohorts.map((c) => (
            <button key={c.id} onClick={() => setSelected(c.access_code)} data-testid={`bg-cohort-card-${c.access_code}`}
              className="rounded-2xl p-5 text-left transition-transform active:scale-[0.99]" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-base font-bold text-white">{c.cohort_name}</span>
                {c.is_demo && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#B4530022", color: "#F0A868" }}>DEMO</span>}
              </div>
              <p className="mt-0.5 text-xs" style={{ color: "#9AA6C2" }}>{c.sponsoring_org} · {c.access_code}</p>
              <div className="mt-3 flex gap-4 text-xs" style={{ color: "#C7CEE0" }}>
                <span><Users className="mr-1 inline h-3.5 w-3.5" style={{ color: TEAL }} />{c.counts?.invited || 0} peserta</span>
                <span><CheckCircle2 className="mr-1 inline h-3.5 w-3.5" style={{ color: GOLD }} />{c.counts?.completed || 0} selesai</span>
                {c.bridged_b2m_code && <span style={{ color: TEAL }}><GitBranch className="mr-1 inline h-3.5 w-3.5" />B2M</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [overview, setOverview] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [actions, setActions] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [section, setSection] = useState("b2m");
  const [navNonce, setNavNonce] = useState(0);
  const [sp] = useSearchParams();
  useEffect(() => {
    const s = sp.get("section");
    if (s === "bg" || s === "b2m") { setSection(s); setSelected(null); }
  }, [sp]);
  const openB2M = useCallback((code) => { setSection("b2m"); setSelected(code); }, []);

  const load = useCallback(() => {
    b2mOverview(token).then(setOverview).catch((e) => { if (e?.response?.status === 401) onLogout(); });
    b2mListCohorts(token).then((r) => setCohorts(r.cohorts)).catch(() => {});
    b2mActions(token).then(setActions).catch(() => {});
  }, [token, onLogout]);
  useEffect(() => { if (view === "list" && section === "b2m") load(); }, [view, section, load]);

  const runReminders = async () => {
    setReminding(true);
    try { const r = await b2mRunReminders(token); toast.success(`${r.reminders_sent} peringatan dihantar.`); }
    catch { toast.error("Gagal menghantar peringatan."); }
    finally { setReminding(false); }
  };

  const Tabs = () => (
    <ConsultantNav active={section} gold={GOLD} ink={INK}
      onSelectSection={(k) => { setSection(k); setSelected(null); setNavNonce((n) => n + 1); }} />
  );

  if (selected) return <CohortDetail code={selected} token={token} onBack={() => { setSelected(null); }} />;
  if (section === "bg") {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8" data-testid="bg-dashboard">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Rocket className="h-6 w-6" style={{ color: GOLD }} />
            <h1 className="font-heading text-2xl font-bold text-white">Growth Factor — Konsultan</h1>
          </div>
          <div className="flex items-center gap-3">
            <Tabs />
            <button data-testid="bg-logout" onClick={onLogout} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white" style={{ border: "1px solid rgba(255,255,255,0.15)" }}><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        <BGSection key={navNonce} token={token} onOpenB2M={openB2M} />
      </div>
    );
  }
  if (view === "create") return <CreateCohort token={token} onBack={() => setView("list")} onCreated={() => setView("list")} />;
  if (view === "reqs") return <RequirementsManager token={token} onBack={() => setView("list")} />;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8" data-testid="b2m-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket className="h-6 w-6" style={{ color: GOLD }} />
          <h1 className="font-heading text-2xl font-bold text-white">Growth Factor — Konsultan</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs />
          <button data-testid="b2m-reqs-btn" onClick={() => setView("reqs")} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white" style={{ border: "1px solid rgba(214,162,90,0.4)" }}><Target className="h-4 w-4" style={{ color: GOLD }} /> Keperluan Eksport</button>
          <button data-testid="b2m-create-cohort-btn" onClick={() => setView("create")} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold" style={{ background: GOLD, color: INK }}><Plus className="h-4 w-4" /> Cipta Kohort</button>
          <button data-testid="b2m-logout" onClick={onLogout} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white" style={{ border: "1px solid rgba(255,255,255,0.15)" }}><LogOut className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="b2m-overview">
        <Stat label="Kohort Aktif" value={overview?.active_cohorts ?? "—"} />
        <Stat label="Jumlah Peserta" value={overview?.total_participants ?? "—"} />
        <Stat label="PRE Selesai" value={overview?.pre_completed ?? "—"} sub={overview ? `${overview.completion_rate}%` : ""} />
        <Stat label="Purata Skor B2M" value={overview?.avg_b2m_score ?? "—"} />
      </div>

      {actions && (actions.total_overdue > 0 || actions.no_pre > 0 || actions.upcoming > 0 || (actions.potential_success_stories || []).length > 0 || actions.certs_pending_review > 0 || (actions.certs_expiring || []).length > 0 || (actions.certs_expired || []).length > 0) && (
        <div className="mt-6 rounded-2xl p-5" style={{ background: "linear-gradient(135deg,#2A1E1E,#1E2647)", border: "1px solid rgba(217,112,90,0.35)" }} data-testid="b2m-actions-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm font-bold text-white"><AlertTriangle className="h-4 w-4" style={{ color: "#E8A87C" }} /> Tindakan Diperlukan Hari Ini</p>
            <button data-testid="b2m-run-reminders" onClick={runReminders} disabled={reminding}
              className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold disabled:opacity-50" style={{ background: GOLD, color: INK }}>
              {reminding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} Hantar Peringatan Automatik
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {actions.total_overdue > 0 && <span className="rounded-full px-3 py-1 font-bold" style={{ background: "rgba(217,112,90,0.2)", color: "#E8A87C" }}>{actions.total_overdue} penilaian tertunggak</span>}
            {actions.no_pre > 0 && <span className="rounded-full px-3 py-1 font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}>{actions.no_pre} belum buat PRE</span>}
            {actions.upcoming > 0 && <span className="rounded-full px-3 py-1 font-bold" style={{ background: "rgba(47,168,159,0.15)", color: TEAL }}>{actions.upcoming} akan tiba (3 hari)</span>}
            {actions.certs_pending_review > 0 && <span className="flex items-center gap-1 rounded-full px-3 py-1 font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}><ShieldCheck className="h-3 w-3" />{actions.certs_pending_review} sijil menunggu semakan</span>}
            {(actions.certs_expired || []).length > 0 && <span className="flex items-center gap-1 rounded-full px-3 py-1 font-bold" style={{ background: "rgba(217,112,90,0.2)", color: "#E8A87C" }}><ShieldAlert className="h-3 w-3" />{actions.certs_expired.length} sijil tamat tempoh</span>}
            {(actions.certs_expiring || []).length > 0 && <span className="flex items-center gap-1 rounded-full px-3 py-1 font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}><Clock className="h-3 w-3" />{actions.certs_expiring.length} sijil hampir luput (90 hari)</span>}
            {(actions.potential_success_stories || []).length > 0 && <span className="flex items-center gap-1 rounded-full px-3 py-1 font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}><Star className="h-3 w-3" />{actions.potential_success_stories.length} bakal success story</span>}
          </div>
          {((actions.certs_expired || []).length > 0 || (actions.certs_expiring || []).length > 0) && (
            <div className="mt-3 space-y-1" data-testid="b2m-cert-alerts">
              {[...(actions.certs_expired || []), ...(actions.certs_expiring || [])].slice(0, 6).map((ct, i) => (
                <p key={i} className="text-[11px]" style={{ color: ct.bucket === "expired" ? "#E8A87C" : "#C7CEE0" }}>
                  <ShieldAlert className="mr-1 inline h-3 w-3" /> {ct.business_name || ct.name} — {ct.cert_name} ({ct.cert_type}): {ct.bucket === "expired" ? "tamat tempoh" : "luput"} {ct.expiry_date}
                </p>
              ))}
            </div>
          )}
          {(actions.potential_success_stories || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="b2m-success-list">
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: GOLD }}>Bakal Kisah Kejayaan</p>
              {actions.potential_success_stories.slice(0, 6).map((ss) => (
                <div key={ss.id} className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-white"><Star className="mr-1 inline h-3 w-3" style={{ color: GOLD }} />{ss.business_name || ss.name} <span style={{ color: TEAL }}>(+{ss.improvement} mata)</span></p>
                  <button data-testid={`b2m-success-dl-${ss.id}`} onClick={async () => { try { const b = await b2mSuccessStory(ss.id, token); downloadBlob(b, `success-story-${ss.id.slice(0, 8)}.pdf`); } catch { toast.error("Gagal jana."); } }}
                    className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(47,168,159,0.15)", color: TEAL }}><Download className="h-3 w-3" /> Kad</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <h2 className="mt-8 mb-3 text-sm font-bold uppercase tracking-wider" style={{ color: "#9AA6C2" }}>Kohort</h2>
      {cohorts === null ? <FullLoader /> : cohorts.length === 0 ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-no-cohorts">
          <LayoutGrid className="mx-auto h-8 w-8" style={{ color: GOLD }} />
          <p className="mt-3 text-sm" style={{ color: "#9AA6C2" }}>Belum ada kohort. Cipta kohort Brand2Market pertama.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {cohorts.map((c) => (
            <button key={c.id} onClick={() => setSelected(c.access_code)} data-testid={`b2m-cohort-card-${c.access_code}`}
              className="rounded-2xl p-5 text-left transition-transform active:scale-[0.99]" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-base font-bold text-white">{c.cohort_name}</span>
                {c.is_demo && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#B4530022", color: "#F0A868" }}>DEMO</span>}
              </div>
              <p className="mt-0.5 text-xs" style={{ color: "#9AA6C2" }}>{c.sponsoring_org} · {c.access_code}</p>
              <div className="mt-3 flex gap-4 text-xs" style={{ color: "#C7CEE0" }}>
                <span><Users className="mr-1 inline h-3.5 w-3.5" style={{ color: TEAL }} />{c.counts?.invited || 0} peserta</span>
                <span><Target className="mr-1 inline h-3.5 w-3.5" style={{ color: GOLD }} />PRE {c.counts?.pre || 0}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function B2MConsultantDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const logout = useCallback(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }, []);
  return (
    <div className="min-h-screen font-body" style={{ background: INK }} data-testid="b2m-consultant">
      {!token ? <LoginGate onLogin={setToken} /> : <Dashboard token={token} onLogout={logout} />}
    </div>
  );
}
